import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  InputAdornment,
  Theme,
  CircularProgress,
  Alert,
  Typography,
  TableSortLabel,
  TablePagination, 
  Button,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { DashboardCustomizeOutlined, Search as SearchIcon } from '@mui/icons-material';
import AppLayout from '../../layouts/AppLayout';
import ProductCreateDialog from '../../components/Products/ProductCreateDialog';
import ProductEditDialog from '../../components/Products/ProductEditDialog';
import { postData, downloadFileWithMetadata } from '../../utils/apiClient';
import { PERMISSIONS } from '../../constants/permissions';
import { useNavigate } from 'react-router-dom';

// Product type definitions
interface Product {
  title: string;
  productUuid: string;
  dateUpdated: number;
  questionsNotSvn: number;
  questionsNotWas: number;
  questionsQap: number;
  questionsSvn: number;
  questionsWas: number;
  eprof: string;
}

interface ProductSearchRequest {
  title?: string;
  sortOrder?: 'asc' | 'desc';
  sortBy?: string;
}

interface ProductSearchResponse {
  products: Product[];
  totalCount: number;
  sortBy: string;
  sortOrder: string;
  title?: string;
}

type SortField = 'title' | 'dateUpdated';
type SortOrder = 'asc' | 'desc';
type SnackbarType = 'success' | 'error' | 'warning' | 'info';

const ProductListContainer: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedProductUuid, setSelectedProductUuid] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]); // Store all fetched data
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState<SortField>('title');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Custom dialog state for SVN update
  const [isUpdateConfirmOpen, setIsUpdateConfirmOpen] = useState(false);
  const [productUuidToUpdate, setProductUuidToUpdate] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateStatus, setUpdateStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  
  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState<SnackbarType>('info');

  // Snackbar functions
  const showSnackbar = useCallback((message: string, type: SnackbarType = 'info') => {
    setSnackbarMessage(message);
    setSnackbarType(type);
    setSnackbarOpen(true);
  }, []);

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // Common function to search products
  const searchProductsApi = async (searchParams: ProductSearchRequest): Promise<ProductSearchResponse> => {
    try {
      const response = await postData(
        '/products',
        PERMISSIONS.GET_PRODUCTS,
        searchParams,
      );
      return response;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  };

  // Common function to get all products
  const getAllProductsApi = useCallback(async (): Promise<ProductSearchResponse> => {
    return await searchProductsApi({
      sortOrder,
      sortBy,
    });
  }, [sortOrder, sortBy]);

  // Function to update paginated products from allProducts
  const updatePaginatedProducts = useCallback(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const paginatedProducts = allProducts.slice(startIndex, endIndex);
    setProducts(paginatedProducts);
  }, [page, rowsPerPage, allProducts]);

  // Update paginated products when page or rowsPerPage changes
  useEffect(() => {
    updatePaginatedProducts();
  }, [updatePaginatedProducts]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllProductsApi();

      if (response.products && response.products.length > 0) {
        setAllProducts(response.products); // Store all data
        setTotalCount(response.totalCount || response.products.length);
        setPage(0); // Reset to first page when fetching new data
      } else {
        setAllProducts([]);
        setProducts([]);
        setTotalCount(0);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch products');
      console.error('Error fetching products:', err);
      showSnackbar('Failed to fetch products. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  }, [getAllProductsApi, showSnackbar]);

  const searchProducts = useCallback(async (title: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await searchProductsApi({
        title,
        sortOrder,
        sortBy,
      });
      console.log('Search API Response:', response); // Debug log

      if (response.products && response.products.length > 0) {
        setAllProducts(response.products); // Store all data
        setTotalCount(response.totalCount || response.products.length);
        setPage(0); // Reset to first page when searching
      } else {
        setAllProducts([]);
        setProducts([]);
        setTotalCount(0);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to search products');
      console.error('Error searching products:', err);
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim()) {
        searchProducts(searchTerm);
      } else {
        fetchProducts();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, sortBy, sortOrder, fetchProducts, searchProducts]);

  const handleSort = (field: SortField) => {
    const isAsc = sortBy === field && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(field);
    setPage(0); // Reset to first page when sorting
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing page size
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleAddProduct = () => {
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleSaveProduct = (data: any) => {
    console.log('Product data to save:', data);
    
    // After saving, refresh the product list
    fetchProducts();
    setSearchTerm('');
    
    // Show success message
    showSnackbar('Product created successfully!', 'success');
  };

  const handleUpdate = (productUuid: string) => {
    console.log('Update clicked for product:', productUuid);
    setProductUuidToUpdate(productUuid);
    setIsUpdateConfirmOpen(true);
  };

  const handleDownloadRefineReport = async (productUuid: string) => {
    try {
      const { blob, filename, contentType } = await downloadFileWithMetadata(
        `/reports/refine?p=${productUuid}`,
        'GET_REFINE_REPORT'
      );

      const downloadUrl = window.URL.createObjectURL(
        contentType ? new Blob([blob], { type: contentType }) : blob
      );
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename ?? `refine-report-${productUuid}`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      showSnackbar('Refine report downloaded successfully', 'success');
    } catch (error: any) {
      console.error('Error downloading refine report:', error);
      showSnackbar(error.message || 'Failed to download refine report. Please try again.', 'error');
    }
  };

  const handleConfirmUpdate = async () => {
    setIsUpdating(true);
    setUpdateStatus('loading');
    try {
      await postData(
        '/svn/update',
        PERMISSIONS.SVN_UPDATE_SF_INIT,
        { productId: productUuidToUpdate }
      );
      // Auto-close dialog after loading completes
      setTimeout(() => {
        handleCloseUpdateDialog();
        setIsUpdating(false);
        showSnackbar('Update initiated. Track progress in the Jobs Dashboard!', 'success');
      }, 2000);
    } catch (error: any) {
      console.error('Error updating SVN:', error);
      setUpdateStatus('error');
      showSnackbar(`Update failed: ${error.message || 'Please try again later.'}`, 'error');
      handleCloseUpdateDialog();
      setIsUpdating(false);
    }
  };

  const handleCancelUpdate = () => {
    setIsUpdateConfirmOpen(false);
    setProductUuidToUpdate('');
    setUpdateStatus('idle');
  };

  const handleCloseUpdateDialog = () => {
    setIsUpdateConfirmOpen(false);
    setProductUuidToUpdate('');
    setUpdateStatus('idle');
  };

  const handleEditProduct = (productUuid: string) => {
    console.log('Opening edit dialog for product UUID:', productUuid);
    setSelectedProductUuid(productUuid);
    setIsEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setSelectedProductUuid('');
  };

  const handleSaveEditProduct = (data: any) => {
    console.log('Product edit data to save:', data);
    // After saving, refresh the product list
    fetchProducts();
    setSearchTerm('');
    
    // Show success message
    showSnackbar('Product updated successfully!', 'success');
  };

  const formatDate = (timestamp: number): string => {
    return new Date(timestamp).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const navigate = useNavigate();
  const navigateToJobList = () => {
    navigate('/jobs');
  }

  return (
    <AppLayout>
      <Box sx={{ p: 3 }}>
        {/* Search and Add Section */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Box>
            <TextField
              placeholder="Search Products"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                width: 300,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box>
            <Button
              onClick={handleAddProduct}
              sx={(theme: Theme) => ({
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              })}
            >
              Add New Product
            </Button>
            <IconButton onClick={navigateToJobList} sx={{ ml: 2 }}>
              <DashboardCustomizeOutlined />
            </IconButton>
          </Box>
        </Box>

        {/* Results Summary */}
        {!loading && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {products.length} of {totalCount} products
            </Typography>
          </Box>
        )}

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Data Table */}
        <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{
                  maxWidth: 300,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  <TableSortLabel
                    active={sortBy === 'title'}
                    direction={sortBy === 'title' ? sortOrder : 'asc'}
                    onClick={() => handleSort('title')}
                  >
                    Product
                  </TableSortLabel>
                </TableCell>
                <TableCell>In WAS</TableCell>
                <TableCell>In SVN</TableCell>
                <TableCell>In QAP</TableCell>
                <TableCell>Not in WAS</TableCell>
                <TableCell>Not in SVN</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'dateUpdated'}
                    direction={sortBy === 'dateUpdated' ? sortOrder : 'asc'}
                    onClick={() => handleSort('dateUpdated')}
                  >
                    Updated
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm ? 'No products found matching your search.' : 'No products available.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product, index) => (
                  <TableRow key={index} hover>
                    <TableCell
                      sx={{
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        py: 2,
                      }}
                      title={product.title}>
                      <Link
                        component="button"
                        variant="body1"
                        onClick={() => handleEditProduct(product.productUuid)}
                        sx={{
                          textAlign: 'left',
                          display: 'block',
                          width: '100%',
                        }}
                      >
                        {product.title}
                      </Link>
                    </TableCell>
                    <TableCell sx={{ py: 6 }}>{product.questionsWas}</TableCell>
                    <TableCell sx={{ py: 6 }}>{product.questionsSvn}</TableCell>
                    <TableCell sx={{ py: 6 }}>{product.questionsQap}</TableCell>
                    <TableCell sx={{ py: 6 }}>{product.questionsNotWas}</TableCell>
                    <TableCell sx={{ py: 6 }}>{product.questionsNotSvn}</TableCell>
                    <TableCell sx={{ py: 6 }}>{formatDate(product.dateUpdated)}</TableCell>
                    <TableCell sx={{ py: 6 }}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
                        <Box>
                          <Link
                            component="button"
                            variant="body2"
                            onClick={() => handleUpdate(product.productUuid)}
                            sx={(theme: Theme) => ({
                              color: theme.palette.primary.main,
                              textDecoration: 'none',
                            })}
                          >
                            UPDATE
                          </Link>
                        </Box>
                          <>
                          |
                            <Box>
                              <Tooltip title="Download Refine Report">
                                <Link
                                  component="button"
                                  variant="body2"
                                  onClick={() => handleDownloadRefineReport(product.productUuid)}
                                  sx={(theme: Theme) => ({
                                    color: theme.palette.primary.main,
                                    textDecoration: 'none',
                                  })}
                                >
                                  DOWNLOAD
                                </Link>
                              </Tooltip>
                            </Box>
                          </>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`}
          />
        </TableContainer>
      </Box>

      <ProductCreateDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSaveProduct}
      />

      <ProductEditDialog
        open={isEditDialogOpen}
        onClose={handleCloseEditDialog}
        onSave={handleSaveEditProduct}
        productUuid={selectedProductUuid}
      />

      {/* Custom Dialog for SVN Update */}
      <Dialog 
        open={isUpdateConfirmOpen} 
        onClose={updateStatus === 'loading' ? undefined : handleCloseUpdateDialog}
        maxWidth="sm" 
        fullWidth
        disableEscapeKeyDown={updateStatus === 'loading'}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {updateStatus === 'idle' ? 'Update Product Data' : 
           updateStatus === 'loading' ? 'Updating Product Data...': ""}
        </DialogTitle>
        <DialogContent>
          {updateStatus === 'idle' && (
            <Typography>
              Are you sure you want to refresh this product's data from SVN and WAS? This will also run the data refinement process.
            </Typography>
          )}
          {updateStatus === 'loading' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
              <CircularProgress sx={{ mb: 2 }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {updateStatus === 'idle' && (
            <>
              <Button onClick={handleCancelUpdate} disabled={isUpdating}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirmUpdate}
                color="primary"
                variant="contained"
                disabled={isUpdating}
              >
                Update
              </Button>
            </>
          )}
          {updateStatus === 'loading' && (
            <Button onClick={handleCancelUpdate} disabled={isUpdating}>
              Cancel
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for custom messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbarType} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
    </AppLayout>
  );
};

export default ProductListContainer;
